import Vue from 'vue'
import App from './App.vue'
import postimgFallbackAvatar from '@/static/pic/litangbao.png'
import router from './router'
import store from './store'
import Vant from 'vant'
import 'vant/lib/index.css'
import './css/overall.css'
import Vue2TouchEvents from 'vue2-touch-events'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range'
import ElRow from 'element-ui/lib/row'
import ElCol from 'element-ui/lib/col'
import ElButton from 'element-ui/lib/button'
import ElCheckbox from 'element-ui/lib/checkbox'
import 'element-ui/lib/theme-chalk/base.css'
import 'element-ui/lib/theme-chalk/row.css'
import 'element-ui/lib/theme-chalk/col.css'
import 'element-ui/lib/theme-chalk/button.css'
import 'element-ui/lib/theme-chalk/checkbox.css'

import remChange from '@/utils/rem'
import { getDeviceType } from '@/utils/device'
import { installPenNativeBridge } from '@/utils/penNativeBridge'
import { safeAvatarUrl } from '@/utils/avatar'
import ws from '@/unitls/ws'
import chat from '@/unitls/chat.js'

const APP_BUILD_VERSION = process.env.VUE_APP_BUILD_VERSION || ''
const APP_BUILD_STORAGE_KEY = 'tr_h5_build_version'
const APP_RELOAD_STORAGE_KEY = 'tr_h5_reload_build_version'

function getHtmlBuildVersion() {
  const meta = document.querySelector('meta[name="app-build-version"]')
  return meta ? meta.getAttribute('content') || '' : ''
}

function clearBrowserCaches() {
  if (!window.caches || !window.caches.keys) return Promise.resolve()
  return window.caches
    .keys()
    .then(keys => Promise.all(keys.map(key => window.caches.delete(key))))
    .catch(() => {})
}

function appendUrlVersion(url, version) {
  try {
    const target = new URL(url, window.location.href)
    target.searchParams.set('_h5v', version || Date.now())
    return target.toString()
  } catch (e) {
    const separator = url.indexOf('?') === -1 ? '?' : '&'
    return `${url}${separator}_h5v=${encodeURIComponent(version || Date.now())}`
  }
}

function reloadFresh(version) {
  const reloadKey = `${version || APP_BUILD_VERSION || 'unknown'}:${window.location.pathname}`
  if (sessionStorage.getItem(APP_RELOAD_STORAGE_KEY) === reloadKey) return false

  sessionStorage.setItem(APP_RELOAD_STORAGE_KEY, reloadKey)
  clearBrowserCaches().finally(() => {
    window.location.replace(appendUrlVersion(window.location.href, version || APP_BUILD_VERSION))
  })
  return true
}

function ensureFreshBuild() {
  const htmlBuildVersion = getHtmlBuildVersion()
  if (htmlBuildVersion && APP_BUILD_VERSION && htmlBuildVersion !== APP_BUILD_VERSION) {
    if (reloadFresh(htmlBuildVersion)) return false
  }

  if (APP_BUILD_VERSION) {
    localStorage.setItem(APP_BUILD_STORAGE_KEY, APP_BUILD_VERSION)
  }
  sessionStorage.removeItem(APP_RELOAD_STORAGE_KEY)
  return true
}

function isChunkLoadError(error) {
  const message = String((error && (error.message || error.reason || error.type)) || '')
  const target = error && error.target
  return (
    /Loading chunk|ChunkLoadError|CSS_CHUNK_LOAD_FAILED|failed to fetch dynamically imported module/i.test(message) ||
    (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK') && /\/(js|css)\//.test(target.src || target.href || ''))
  )
}

function installBuildCacheGuard() {
  window.addEventListener(
    'error',
    event => {
      if (isChunkLoadError(event)) reloadFresh(getHtmlBuildVersion())
    },
    true
  )
  window.addEventListener('unhandledrejection', event => {
    if (isChunkLoadError(event.reason)) reloadFresh(getHtmlBuildVersion())
  })
}

function installPostimgAvatarFallback() {
  document.addEventListener(
    'error',
    event => {
      const image = event.target
      if (!image || image.tagName !== 'IMG') return

      const source = image.currentSrc || image.src || ''
      if (!/^https?:\/\/i\.postimg\.cc\//i.test(source) || image.dataset.postimgFallbackApplied) return

      image.dataset.postimgFallbackApplied = 'true'
      image.src = postimgFallbackAvatar
    },
    true
  )
}

// 数字输入项用于直接录入新数值时，获得焦点后清除已有值，避免光标位置导致误编辑。
function installNumberInputClearOnFocus() {
  const isNumberInput = input => {
    const isNativeNumber = input && input.type === 'number'
    const isVantNumber =
      input &&
      input.classList.contains('van-field__control') &&
      ['decimal', 'numeric'].includes(input.getAttribute('inputmode'))
    return input && input.tagName === 'INPUT' && (isNativeNumber || isVantNumber) && !input.disabled && !input.readOnly
  }

  document.addEventListener('focusin', event => {
    const input = event.target
    if (!isNumberInput(input)) return

    input.dataset.clearOnFocusValue = input.value
    input.dataset.clearOnFocusChanged = 'false'
    input.value = ''
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }, true)

  document.addEventListener('input', event => {
    const input = event.target
    if (input && input.dataset.clearOnFocusValue !== undefined && event.isTrusted) {
      input.dataset.clearOnFocusChanged = 'true'
    }
  }, true)

  document.addEventListener('focusout', event => {
    const input = event.target
    if (!input || input.dataset.clearOnFocusValue === undefined) return

    if (input.dataset.clearOnFocusChanged === 'false') {
      input.value = input.dataset.clearOnFocusValue
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }
    delete input.dataset.clearOnFocusValue
    delete input.dataset.clearOnFocusChanged
  }, true)
}

function safeJsonParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback
  } catch (e) {
    return fallback
  }
}

function clearPenCache() {
  const exactKeys = [
    'BTaddress',
    'BTname',
    'BTpenCacheUid',
    'BTdeviceStatus',
    'BTinsulinRecordList',
    'BTinsulinRecordList_kser',
    'BTinsulinRecordList_wzb'
  ]
  exactKeys.forEach(key => localStorage.removeItem(key))

  const removeKeys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.indexOf('BTdeviceStatus_') === 0) removeKeys.push(key)
  }
  removeKeys.forEach(key => localStorage.removeItem(key))
}

window.getUserByAndroid = function (info) {
  try {
    const data = typeof info === 'string' ? JSON.parse(info) : info
    const appuser = data && data.appuser ? data.appuser : {}
    const token = data && data.token ? String(data.token) : ''
    const oldAppuser = safeJsonParse(localStorage.getItem('appuser'), {})

    if (oldAppuser.id && appuser.id && String(oldAppuser.id) !== String(appuser.id)) {
      clearPenCache()
    }

    const targetHigh = appuser.targetHigh ? appuser.targetHigh : '10'
    const targetLow = appuser.targetLow ? appuser.targetLow : '3.9'
    localStorage.setItem('targetHigh', targetHigh)
    localStorage.setItem('targetLow', targetLow)

    localStorage.setItem('token', token)
    localStorage.setItem('appuser', JSON.stringify(data.appuser))
    localStorage.setItem('loginStatus', 'true')
  } catch (e) {
    console.error(e)
  }

  try {
    window.dispatchEvent(new Event('tr-appuser-updated'))
  } catch (e) {}
}

Vue.prototype.$ws = ws
ws.type10 = (data) => {
  const chatid = JSON.parse(data).data
  chat.getChatMsg(chatid) // 消息
}
ws.type11 = () => {
  chat.getChatList() // 进出群?
}

remChange()

Vue.use(Vue2TouchEvents)
Vue.prototype.$safeAvatar = safeAvatarUrl
Vue.prototype.$moment = moment
Vue.use(Vant)
Vue.component(ElRow.name, ElRow)
Vue.component(ElCol.name, ElCol)
Vue.component(ElButton.name, ElButton)
Vue.component(ElCheckbox.name, ElCheckbox)
Vue.config.productionTip = false

async function setupHeavyPlugins(device) {
  if (device === 'watch') return

  const [{ default: ElementUI }, { default: Vue2OrgTree }] = await Promise.all([
    import('element-ui'),
    import('vue2-org-tree'),
    import('element-ui/lib/theme-chalk/index.css'),
    import('vue2-org-tree/dist/style.css')
  ])

  Vue.use(ElementUI)
  Vue.use(Vue2OrgTree)
}

function setupHeavyPluginsAfterFirstPaint(device) {
  if (device === 'watch') return

  const load = () => {
    setupHeavyPlugins(device).catch(error => {
      console.error('重型插件加载失败', error)
    })
  }

  // 先让 Vue 完成首屏渲染，再在后续帧加载非首屏依赖。
  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      window.setTimeout(load, 0)
    })
  } else {
    window.setTimeout(load, 0)
  }
}

function saveVersionFromUrl() {
  const url = window.location.href
  const urlStr = url.split('v=')[1]
  if (urlStr) localStorage.setItem('version', urlStr)
}

function stopPrefetchForWatch() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  if (!document.head || !window.MutationObserver) return

  const removePrefetchLinks = (nodes) => {
    nodes.forEach((node) => {
      if (node && node.tagName === 'LINK' && node.rel === 'prefetch') {
        if (node.parentNode) node.parentNode.removeChild(node)
      }
    })
  }

  removePrefetchLinks(Array.from(document.querySelectorAll('link[rel="prefetch"]')))

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes && mutation.addedNodes.length) {
        removePrefetchLinks(Array.from(mutation.addedNodes))
      }
    })
  })

  observer.observe(document.head, { childList: true })
}

function bootstrap() {
  installBuildCacheGuard()
  installPostimgAvatarFallback()
  installNumberInputClearOnFocus()
  if (!ensureFreshBuild()) return

  const device = getDeviceType()
  if (device === 'watch') {
    stopPrefetchForWatch()
  }

  saveVersionFromUrl()
  installPenNativeBridge()

  new Vue({
    store,
    router,
    render: (h) => h(App)
  }).$mount('#app')

  setupHeavyPluginsAfterFirstPaint(device)
}

bootstrap()
